"use client";

import { c as _c } from "react/compiler-runtime";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "cn";
import type * as React from "react";
import { useState } from "react";
interface SliderProps extends React.ComponentProps<typeof SliderPrimitive.Root> {
  /**
   * Returns the `aria-valuetext` for a thumb's value, which screen readers
   * announce instead of the raw number (e.g. `"$80,000"` for `80000`).
   */
  getAriaValueText?: (value: number) => string;
}

/**
 * An input that lets the user select a value within a given range.
 *
 * It supports multiple thumbs on the slider by passing an array of values.
 */
function Slider(t0) {
  const $ = _c(37);
  let ariaLabel;
  let ariaLabelledBy;
  let className;
  let defaultValue;
  let getAriaValueText;
  let onValueChange;
  let props;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      value,
      defaultValue,
      min: t1,
      max: t2,
      getAriaValueText,
      onValueChange,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = ariaLabelledBy;
    $[3] = className;
    $[4] = defaultValue;
    $[5] = getAriaValueText;
    $[6] = onValueChange;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
    $[10] = value;
  } else {
    ariaLabel = $[1];
    ariaLabelledBy = $[2];
    className = $[3];
    defaultValue = $[4];
    getAriaValueText = $[5];
    onValueChange = $[6];
    props = $[7];
    t1 = $[8];
    t2 = $[9];
    value = $[10];
  }
  const min = t1 === undefined ? 0 : t1;
  const max = t2 === undefined ? 100 : t2;
  let t3;
  if ($[11] !== defaultValue || $[12] !== min) {
    t3 = defaultValue ?? [min];
    $[11] = defaultValue;
    $[12] = min;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const [uncontrolledValues, setUncontrolledValues] = useState(t3);
  const values = value ?? uncontrolledValues;
  let t4;
  if ($[14] !== onValueChange) {
    t4 = nextValues => {
      setUncontrolledValues(nextValues);
      onValueChange?.(nextValues);
    };
    $[14] = onValueChange;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== className) {
    t5 = cn("relative flex w-full touch-none items-center select-none", "data-disabled:opacity-50", className);
    $[16] = className;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SliderPrimitive.Track data-slot="slider-track" className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-(--bg-tertiary)"><SliderPrimitive.Range data-slot="slider-range" className="absolute h-full bg-primary" /></SliderPrimitive.Track>;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== ariaLabel || $[20] !== ariaLabelledBy || $[21] !== getAriaValueText || $[22] !== values) {
    let t8;
    if ($[24] !== ariaLabel || $[25] !== ariaLabelledBy || $[26] !== getAriaValueText) {
      t8 = (thumbValue, index) => <SliderPrimitive.Thumb data-slot="slider-thumb" key={index} aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} aria-valuetext={getAriaValueText?.(thumbValue)} className={cn("block size-4 shrink-0 cursor-grab rounded-full", "border border-primary bg-white shadow-sm outline-none", "transition-[color,box-shadow] active:cursor-grabbing", "ring-ring/50 hover:ring-4 focus-visible:ring-4", "data-disabled:pointer-events-none")} />;
      $[24] = ariaLabel;
      $[25] = ariaLabelledBy;
      $[26] = getAriaValueText;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    t7 = values.map(t8);
    $[19] = ariaLabel;
    $[20] = ariaLabelledBy;
    $[21] = getAriaValueText;
    $[22] = values;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[28] !== defaultValue || $[29] !== max || $[30] !== min || $[31] !== props || $[32] !== t4 || $[33] !== t5 || $[34] !== t7 || $[35] !== value) {
    t8 = <SliderPrimitive.Root data-slot="slider" value={value} defaultValue={defaultValue} min={min} max={max} onValueChange={t4} className={t5} {...props}>{t6}{t7}</SliderPrimitive.Root>;
    $[28] = defaultValue;
    $[29] = max;
    $[30] = min;
    $[31] = props;
    $[32] = t4;
    $[33] = t5;
    $[34] = t7;
    $[35] = value;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  return t8;
}
export { Slider, type SliderProps };